// src/components/qr-customer/FridgeCheckout.jsx
// ✅ Auth gate: must login before checkout
// ✅ Fully responsive mobile header
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { useCustomerAuth } from '../../context/CustomerAuthContext';
import { fridgeCheckout, resolveImageUrl } from '../../services/api';
import WhatsAppAuthModal from '../shared/WhatsAppAuthModal';
import CustomerProfile from '../shared/CustomerProfile';
import toast from 'react-hot-toast';
import {
  ArrowLeftIcon, ShieldCheckIcon, LockClosedIcon,
  MapPinIcon, ShoppingBagIcon,
  UserIcon, ArrowRightOnRectangleIcon, UserCircleIcon,
} from '@heroicons/react/24/outline';

const RAZORPAY_KEY = process.env.REACT_APP_RAZORPAY_KEY_ID || '';
const EMOJI_MAP = { milk:'🥛', egg:'🥚', butter:'🧈', cheese:'🧀', curd:'🍶', yogurt:'🍶', paneer:'🧀', ghee:'🫙' };
const getEmoji  = (name='') => { const n=name.toLowerCase(); for(const [k,v] of Object.entries(EMOJI_MAP)) if(n.includes(k)) return v; return '🛒'; };
const getImgSrc = (item) => { const raw=item?.image_url||item?.imageUrl||item?.image||null; if(!raw) return null; try { return resolveImageUrl(raw); } catch { return raw; } };

const loadRazorpay = () => new Promise(resolve => {
  if (window.Razorpay) return resolve(true);
  const s = document.createElement('script');
  s.src = 'https://checkout.razorpay.com/v1/checkout.js';
  s.onload = () => resolve(true); s.onerror = () => resolve(false);
  document.body.appendChild(s);
});

const FridgeCheckout = () => {
  const location  = useLocation();
  const navigate  = useNavigate();
  const { cart, getCartTotal, clearCart }          = useCart();
  const { customer, isLoggedIn, logout, authLoading } = useCustomerAuth();

  const [loading,      setLoading]      = useState(false);
  const [imgErrors,    setImgErrors]    = useState({});
  const [showAuth,     setShowAuth]     = useState(false);
  const [showProfile,  setShowProfile]  = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  const { fridgeId, fridgeName, apartment } = location.state || {};

  useEffect(() => { if (!fridgeId) navigate('/scan'); }, [fridgeId, navigate]);

  // Auto-open auth if not logged in
  useEffect(() => {
    if (!authLoading && !isLoggedIn) setShowAuth(true);
  }, [authLoading, isLoggedIn]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!showDropdown) return;
    const fn = () => setShowDropdown(false);
    setTimeout(() => document.addEventListener('click', fn), 0);
    return () => document.removeEventListener('click', fn);
  }, [showDropdown]);

  if (!fridgeId || !cart.length) return null;

  const handleLogout = () => { logout(); setShowDropdown(false); toast.success('Logged out 👋'); };

  const handlePayment = async () => {
    if (!isLoggedIn) { setShowAuth(true); return; }
    setLoading(true);
    try {
      const loaded = await loadRazorpay();
      if (!loaded) { toast.error('Payment gateway failed to load.'); setLoading(false); return; }
      const options = {
        key:         RAZORPAY_KEY,
        amount:      Math.round(parseFloat(getCartTotal()) * 100),
        currency:    'INR',
        name:        'Fresh Hills',
        description: `Fridge — ${apartment} / ${fridgeName}`,
        image:       '/images/logo.png',
        prefill: {
          name:    customer?.name  || '',
          contact: customer?.phone ? `91${customer.phone}` : '',
        },
        handler: async (rz) => {
          try {
            const res = await fridgeCheckout({
              fridge_id:           fridgeId,
              total_amount:        getCartTotal(),
              payment_method:      'razorpay',
              razorpay_payment_id: rz.razorpay_payment_id,
              customer_id:         customer?.id || null,
              items: cart.map(i => ({
                product_id: i.product_id,
                quantity:   i.quantity || 1,
                price:      Number(i.price),
              })),
            });
            if (res.data.success) {
              clearCart();
              navigate('/payment-success', {
                state: { orderNumber: res.data.order_number, amount: getCartTotal(), paymentId: rz.razorpay_payment_id },
              });
              toast.success('Payment successful! Take your products 🎉');
            } else {
              toast.error(res.data.message || 'Order save failed. Contact staff.');
            }
          } catch {
            toast.error('Payment done but order save failed. Payment ID: ' + rz.razorpay_payment_id);
          } finally { setLoading(false); }
        },
        notes: { fridge_id: fridgeId, apartment: apartment||'', location: fridgeName||'' },
        theme: { color: '#059669' },
        modal: { ondismiss: () => { setLoading(false); toast('Payment cancelled.', { icon: 'ℹ️' }); } },
      };
      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', resp => { setLoading(false); toast.error('Payment failed: '+(resp.error?.description||'')); });
      rzp.open();
    } catch { toast.error('Something went wrong.'); setLoading(false); }
  };

  const css = `
    @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800;900&display=swap');
    *,*::before,*::after{margin:0;padding:0;box-sizing:border-box;}
    html{-webkit-text-size-adjust:100%;}
    body{font-family:'Nunito',sans-serif;background:#f0fdf4;overflow-x:hidden;}

    /* ─── PAGE ─── */
    .ck-pg{min-height:100vh;background:linear-gradient(150deg,#f0fdf4 0%,#fff 55%,#f0f9ff 100%);position:relative;overflow:hidden;}
    .ck-blob{position:absolute;border-radius:50%;filter:blur(70px);pointer-events:none;z-index:0;}
    .ck-b1{width:16rem;height:16rem;background:#a7f3d0;opacity:.13;top:-3rem;left:-3rem;}
    .ck-b2{width:13rem;height:13rem;background:#bae6fd;opacity:.1;bottom:-2rem;right:-2rem;}

    /* ─── HEADER ─── */
    .ck-hdr{position:sticky;top:0;z-index:50;background:rgba(255,255,255,.96);backdrop-filter:blur(14px);border-bottom:1px solid rgba(5,150,105,.12);box-shadow:0 2px 14px rgba(0,0,0,.05);}
    .ck-hdr-in{max-width:760px;margin:0 auto;padding:0 .875rem;display:flex;align-items:center;justify-content:space-between;height:54px;gap:.5rem;}
    @media(min-width:640px){.ck-hdr-in{height:62px;padding:0 1.5rem;}}
    .ck-logo{display:flex;align-items:center;cursor:pointer;flex-shrink:0;}
    .ck-logo img{height:30px;object-fit:contain;}
    @media(min-width:480px){.ck-logo img{height:34px;}}
    @media(min-width:768px){.ck-logo img{height:38px;}}
    .ck-hdr-r{display:flex;align-items:center;gap:.35rem;flex-shrink:0;}
    @media(min-width:480px){.ck-hdr-r{gap:.5rem;}}

    /* back button */
    .ck-back{display:flex;align-items:center;gap:.25rem;background:#f0fdf4;color:#059669;border:1.5px solid #a7f3d0;border-radius:9999px;padding:.28rem .65rem;font-weight:700;font-size:.7rem;cursor:pointer;font-family:'Nunito',sans-serif;transition:all .2s;white-space:nowrap;flex-shrink:0;}
    .ck-back svg{width:.62rem;height:.62rem;}
    .ck-back:hover{background:#059669;color:white;border-color:#059669;}
    @media(min-width:480px){.ck-back{font-size:.75rem;padding:.3rem .8rem;}}

    /* login btn */
    .ck-lbtn{display:flex;align-items:center;gap:.25rem;background:white;color:#059669;border:1.5px solid #d1fae5;font-weight:700;font-size:.7rem;padding:.28rem .65rem;border-radius:9999px;cursor:pointer;transition:all .2s;white-space:nowrap;font-family:'Nunito',sans-serif;flex-shrink:0;}
    .ck-lbtn:hover{background:#f0fdf4;}
    .ck-lbtn svg{width:.7rem;height:.7rem;}
    @media(min-width:480px){.ck-lbtn{font-size:.75rem;padding:.3rem .8rem;}}

    /* profile button */
    .ck-pw{position:relative;display:inline-flex;flex-shrink:0;}
    .ck-pb{display:flex;align-items:center;gap:.25rem;background:#ecfdf5;border:1.5px solid #a7f3d0;color:#047857;font-weight:700;font-size:.7rem;padding:.22rem .55rem .22rem .22rem;border-radius:9999px;cursor:pointer;transition:all .2s;font-family:'Nunito',sans-serif;white-space:nowrap;}
    .ck-pb:hover{background:#d1fae5;}
    .ck-av{width:1.4rem;height:1.4rem;background:linear-gradient(135deg,#10b981,#059669);border-radius:50%;display:flex;align-items:center;justify-content:center;color:white;font-size:.58rem;font-weight:800;flex-shrink:0;}
    @media(min-width:380px){.ck-av{width:1.55rem;height:1.55rem;}}
    .ck-pnm{display:none;}
    @media(min-width:500px){.ck-pnm{display:block;max-width:58px;overflow:hidden;text-overflow:ellipsis;font-size:.72rem;}}

    /* dropdown */
    .ck-dd{position:absolute;top:calc(100% + 8px);right:0;background:white;border-radius:1.1rem;padding:.35rem;box-shadow:0 16px 40px rgba(0,0,0,.14);border:1px solid #f3f4f6;min-width:195px;z-index:200;animation:ckDd .18s ease-out;}
    @keyframes ckDd{from{opacity:0;transform:translateY(-6px)}to{opacity:1;transform:translateY(0)}}
    .ck-dd-hd{padding:.65rem .9rem;border-bottom:1px solid #f3f4f6;margin-bottom:.2rem;}
    .ck-dd-av{width:2.25rem;height:2.25rem;background:linear-gradient(135deg,#10b981,#059669);border-radius:50%;display:flex;align-items:center;justify-content:center;color:white;font-size:.88rem;font-weight:800;margin-bottom:.3rem;}
    .ck-dd-nm{font-weight:800;font-size:.88rem;color:#111827;}
    .ck-dd-ph{font-size:.7rem;color:#6b7280;margin-top:.06rem;}
    .ck-dd-it{display:flex;align-items:center;gap:.5rem;padding:.5rem .85rem;border-radius:.6rem;font-size:.82rem;font-weight:600;color:#374151;cursor:pointer;border:none;background:none;width:100%;font-family:'Nunito',sans-serif;transition:background .15s;}
    .ck-dd-it:hover{background:#f0fdf4;color:#059669;}
    .ck-dd-it.dng:hover{background:#fff1f2;color:#ef4444;}
    .ck-dd-it svg{width:.88rem;height:.88rem;flex-shrink:0;}

    /* ─── AUTH GATE ─── */
    .ck-gate{min-height:calc(100vh - 54px);display:flex;flex-direction:column;align-items:center;justify-content:center;padding:2rem 1rem;text-align:center;}
    @media(min-width:640px){.ck-gate{min-height:calc(100vh - 62px);}}
    .ck-gate-ico{font-size:3rem;margin-bottom:.9rem;}
    .ck-gate-t{font-size:1.25rem;font-weight:900;color:#111827;margin-bottom:.45rem;}
    @media(min-width:480px){.ck-gate-t{font-size:1.45rem;}}
    .ck-gate-s{font-size:.86rem;color:#6b7280;line-height:1.65;margin-bottom:1.6rem;max-width:300px;}
    .ck-gate-btn{display:inline-flex;align-items:center;gap:.4rem;background:linear-gradient(135deg,#10b981,#059669);color:white;font-weight:800;font-size:.92rem;padding:.7rem 1.6rem;border-radius:9999px;border:none;cursor:pointer;font-family:'Nunito',sans-serif;box-shadow:0 6px 16px rgba(16,185,129,.3);transition:all .3s;}
    .ck-gate-btn:hover{transform:translateY(-2px);box-shadow:0 10px 22px rgba(16,185,129,.38);}
    .ck-gate-btn svg{width:.9rem;height:.9rem;}

    /* ─── USER BANNER ─── */
    .ck-ubanner{background:linear-gradient(135deg,#ecfdf5,#e0f2fe);border:1px solid #a7f3d0;border-radius:1rem;padding:.7rem .9rem;margin-bottom:.85rem;display:flex;align-items:center;justify-content:space-between;gap:.6rem;}
    .ck-ubanner-l{display:flex;align-items:center;gap:.55rem;min-width:0;}
    .ck-u-av{width:1.9rem;height:1.9rem;background:linear-gradient(135deg,#10b981,#059669);border-radius:50%;display:flex;align-items:center;justify-content:center;color:white;font-size:.75rem;font-weight:800;flex-shrink:0;}
    .ck-u-nm{font-size:.8rem;font-weight:700;color:#065f46;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
    .ck-u-ph{font-size:.68rem;color:#047857;}
    .ck-u-ord{font-size:.7rem;font-weight:700;color:#059669;background:white;border:1px solid #a7f3d0;border-radius:9999px;padding:.18rem .6rem;cursor:pointer;white-space:nowrap;flex-shrink:0;transition:all .2s;font-family:'Nunito',sans-serif;}
    .ck-u-ord:hover{background:#f0fdf4;}

    /* ─── BODY ─── */
    .ck-body{position:relative;z-index:2;max-width:760px;margin:0 auto;padding:1.1rem .875rem 7rem;}
    @media(min-width:640px){.ck-body{padding:1.5rem 1.5rem 7.5rem;}}
    .ck-title{font-size:1.2rem;font-weight:900;color:#111827;margin-bottom:.1rem;}
    @media(min-width:480px){.ck-title{font-size:1.4rem;}}
    .ck-sub{font-size:.72rem;color:#64748b;font-weight:600;margin-bottom:1.1rem;}

    /* cards */
    .ck-card{background:white;border-radius:1.1rem;border:1px solid #f1f5f9;box-shadow:0 3px 14px rgba(0,0,0,.04);margin-bottom:.8rem;overflow:hidden;}
    .ck-loc{display:flex;align-items:center;gap:.7rem;padding:.85rem 1rem;}
    .ck-loc-ic{width:36px;height:36px;border-radius:.6rem;flex-shrink:0;background:linear-gradient(135deg,#ecfdf5,#e0f2fe);display:flex;align-items:center;justify-content:center;}
    .ck-loc-ic svg{width:.95rem;height:.95rem;color:#059669;}
    .ck-loc-lbl{font-size:.6rem;font-weight:700;color:#94a3b8;text-transform:uppercase;letter-spacing:.5px;}
    .ck-loc-nm{font-size:.86rem;font-weight:800;color:#111827;margin-top:.03rem;}
    .ck-sec-t{font-size:.68rem;font-weight:800;color:#374151;padding:.8rem 1rem 0;text-transform:uppercase;letter-spacing:.5px;}

    /* order items */
    .ck-item{display:flex;align-items:center;justify-content:space-between;padding:.65rem 1rem;border-bottom:1px solid #f8fafc;gap:.45rem;}
    .ck-item:last-child{border-bottom:none;}
    .ck-item-l{display:flex;align-items:center;gap:.55rem;flex:1;min-width:0;}
    .ck-item-img{width:38px;height:38px;border-radius:.6rem;flex-shrink:0;background:#fff;border:1px solid #e5e7eb;display:flex;align-items:center;justify-content:center;overflow:hidden;}
    @media(min-width:480px){.ck-item-img{width:42px;height:42px;}}
    .ck-item-img img{width:100%;height:100%;object-fit:contain;padding:.12rem;display:block;}
    .ck-item-img span{font-size:1.1rem;line-height:1;}
    .ck-item-nm{font-weight:700;color:#111827;font-size:.8rem;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
    @media(min-width:480px){.ck-item-nm{font-size:.84rem;}}
    .ck-item-qty{font-size:.62rem;color:#94a3b8;font-weight:600;margin-top:.03rem;}
    .ck-item-pr{font-weight:800;color:#059669;font-size:.84rem;flex-shrink:0;white-space:nowrap;}

    /* total row */
    .ck-total{display:flex;align-items:center;justify-content:space-between;padding:.85rem 1rem;background:linear-gradient(135deg,#ecfdf5,#e0f2fe);}
    .ck-total-lbl{font-weight:800;color:#111827;font-size:.85rem;}
    .ck-total-amt{font-size:1.4rem;font-weight:900;background:linear-gradient(135deg,#059669,#0891b2);-webkit-background-clip:text;-webkit-text-fill-color:transparent;}

    /* razorpay banner */
    .ck-rpay{display:flex;align-items:center;gap:.55rem;background:linear-gradient(135deg,#fff7ed,#fefce8);border:1px solid #fcd34d;border-radius:.9rem;padding:.75rem .9rem;margin-bottom:.8rem;font-size:.76rem;color:#78350f;font-weight:600;}
    .ck-rpay strong{color:#059669;}

    /* trust row */
    .ck-trust{display:flex;align-items:center;justify-content:center;gap:.85rem;padding:.75rem 1rem;flex-wrap:wrap;border-top:1px solid #f8fafc;}
    .ck-trust-i{display:flex;align-items:center;gap:.28rem;font-size:.62rem;font-weight:700;color:#64748b;}
    .ck-trust-i svg{width:.7rem;height:.7rem;color:#059669;}

    /* ─── PAY BAR ─── */
    .ck-paybar{position:fixed;bottom:0;left:0;right:0;z-index:50;background:rgba(255,255,255,.97);backdrop-filter:blur(14px);border-top:1px solid rgba(5,150,105,.15);padding:.8rem .875rem;box-shadow:0 -5px 18px rgba(0,0,0,.06);}
    @media(min-width:640px){.ck-paybar{padding:.9rem 1.5rem;}}
    .ck-paybar-in{max-width:760px;margin:0 auto;}
    .ck-pay-btn{width:100%;padding:.85rem;background:linear-gradient(135deg,#10b981,#059669);color:white;font-weight:900;font-size:.9rem;border:none;border-radius:9999px;cursor:pointer;transition:all .3s;font-family:'Nunito',sans-serif;box-shadow:0 4px 14px rgba(16,185,129,.32);display:flex;align-items:center;justify-content:center;gap:.45rem;}
    @media(min-width:480px){.ck-pay-btn{font-size:.95rem;}}
    .ck-pay-btn:hover:not(:disabled){transform:translateY(-2px);box-shadow:0 8px 22px rgba(16,185,129,.4);}
    .ck-pay-btn:disabled{opacity:.65;cursor:not-allowed;transform:none;}
    .ck-pay-note{text-align:center;font-size:.6rem;color:#94a3b8;font-weight:600;margin-top:.35rem;}
    .ck-spin{width:.88rem;height:.88rem;border:2px solid rgba(255,255,255,.4);border-top-color:white;border-radius:50%;animation:ckSp .7s linear infinite;display:inline-block;flex-shrink:0;}
    @keyframes ckSp{to{transform:rotate(360deg);}}

    /* loader */
    .ck-loading{min-height:100vh;display:flex;align-items:center;justify-content:center;background:#f9fafb;}
    .ck-spinner{width:2.75rem;height:2.75rem;border-radius:50%;border:3px solid #e5e7eb;border-top-color:#059669;animation:ckSpin .8s linear infinite;}
    @keyframes ckSpin{to{transform:rotate(360deg);}}
  `;

  if (authLoading) return (
    <><style>{css}</style><div className="ck-loading"><div className="ck-spinner"/></div></>
  );

  // ── Shared header ─────────────────────────────────────────────────────────
  const Header = () => (
    <header className="ck-hdr">
      <div className="ck-hdr-in">
        <div className="ck-logo" onClick={() => navigate('/')}>
          <img src="/images/logo.png" alt="Fresh Hills"
            onError={e=>{e.target.onerror=null;e.target.style.display='none';}}/>
        </div>
        <div className="ck-hdr-r">
          {isLoggedIn ? (
            <div className="ck-pw" onClick={e=>{e.stopPropagation();setShowDropdown(p=>!p);}}>
              <button className="ck-pb">
                <div className="ck-av">{(customer?.name||'U').charAt(0).toUpperCase()}</div>
                <span className="ck-pnm">{(customer?.name||customer?.phone||'').split(' ')[0]}</span>
              </button>
              {showDropdown && (
                <div className="ck-dd">
                  <div className="ck-dd-hd">
                    <div className="ck-dd-av">{(customer?.name||'U').charAt(0).toUpperCase()}</div>
                    <div className="ck-dd-nm">{customer?.name||'Customer'}</div>
                    <div className="ck-dd-ph">📱 +91 {customer?.phone}</div>
                  </div>
                  <button className="ck-dd-it" onClick={()=>{setShowDropdown(false);setShowProfile(true);}}>
                    <ShoppingBagIcon/>My Orders
                  </button>
                  <button className="ck-dd-it" onClick={()=>{setShowDropdown(false);setShowProfile(true);}}>
                    <UserCircleIcon/>My Profile
                  </button>
                  <button className="ck-dd-it dng" onClick={handleLogout}>
                    <ArrowRightOnRectangleIcon/>Logout
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button className="ck-lbtn" onClick={()=>setShowAuth(true)}>
              <UserIcon/>Login
            </button>
          )}
          <button className="ck-back" onClick={() => navigate(-1)}>
            <ArrowLeftIcon/> Back
          </button>
        </div>
      </div>
    </header>
  );

  return (
    <>
      <style>{css}</style>
      {showAuth    && <WhatsAppAuthModal onSuccess={()=>setShowAuth(false)} onClose={()=>setShowAuth(false)}/>}
      {showProfile && <CustomerProfile onClose={()=>setShowProfile(false)}/>}

      {/* ── AUTH GATE (not logged in) ── */}
      {!isLoggedIn && (
        <div style={{minHeight:'100vh',background:'linear-gradient(150deg,#f0fdf4,#fff 55%,#f0f9ff)'}}>
          <Header/>
          <div className="ck-gate">
            <div className="ck-gate-ico">🔐</div>
            <div className="ck-gate-t">Login to Continue</div>
            <p className="ck-gate-s">
              Login with your WhatsApp number to complete your purchase and track your orders.
            </p>
            <button className="ck-gate-btn" onClick={()=>setShowAuth(true)}>
              <UserIcon/> Login via WhatsApp OTP
            </button>
          </div>
        </div>
      )}

      {/* ── CHECKOUT (logged in) ── */}
      {isLoggedIn && (
        <div className="ck-pg">
          <div className="ck-blob ck-b1"/><div className="ck-blob ck-b2"/>

          <Header/>

          <div className="ck-body">
            <div className="ck-title">Checkout</div>
            <div className="ck-sub">Review your order and pay securely via Razorpay</div>

            {/* User banner */}
            <div className="ck-ubanner">
              <div className="ck-ubanner-l">
                <div className="ck-u-av">{(customer?.name||'U').charAt(0).toUpperCase()}</div>
                <div style={{minWidth:0}}>
                  <div className="ck-u-nm">{customer?.name||'Customer'}</div>
                  <div className="ck-u-ph">📱 +91 {customer?.phone}</div>
                </div>
              </div>
              <button className="ck-u-ord" onClick={()=>setShowProfile(true)}>
                My Orders 📦
              </button>
            </div>

            {/* Fridge location */}
            <div className="ck-card">
              <div className="ck-loc">
                <div className="ck-loc-ic"><MapPinIcon/></div>
                <div>
                  <div className="ck-loc-lbl">Fridge Location</div>
                  <div className="ck-loc-nm">{apartment} — {fridgeName}</div>
                </div>
              </div>
            </div>

            {/* Order items */}
            <div className="ck-card">
              <div className="ck-sec-t">Order Summary</div>
              {cart.map((item, i) => {
                const imgSrc = getImgSrc(item);
                const key    = `ck_${item.product_id ?? item.id ?? i}`;
                return (
                  <div key={key} className="ck-item">
                    <div className="ck-item-l">
                      <div className="ck-item-img">
                        {imgSrc && !imgErrors[key]
                          ? <img src={imgSrc} alt={item.name} loading="lazy"
                              onError={()=>setImgErrors(p=>({...p,[key]:true}))}/>
                          : <span>{getEmoji(item.name)}</span>}
                      </div>
                      <div style={{minWidth:0}}>
                        <div className="ck-item-nm">{item.name}</div>
                        <div className="ck-item-qty">Qty: {item.quantity||1}</div>
                      </div>
                    </div>
                    <div className="ck-item-pr">₹{(item.price*(item.quantity||1)).toFixed(2)}</div>
                  </div>
                );
              })}
              <div className="ck-total">
                <span className="ck-total-lbl">Total Amount</span>
                <span className="ck-total-amt">₹{getCartTotal()}</span>
              </div>
            </div>

            {/* Razorpay banner */}
            <div className="ck-rpay">
              🔒 Secured by <strong>Razorpay</strong> — UPI, Cards, Net Banking &amp; Wallets accepted
            </div>

            {/* Trust */}
            <div className="ck-card">
              <div className="ck-trust">
                <div className="ck-trust-i"><ShieldCheckIcon/> 100% Secure</div>
                <div className="ck-trust-i"><LockClosedIcon/> SSL Encrypted</div>
                <div className="ck-trust-i"><ShoppingBagIcon/> Instant Confirmation</div>
              </div>
            </div>
          </div>

          {/* Sticky pay bar */}
          <div className="ck-paybar">
            <div className="ck-paybar-in">
              <button className="ck-pay-btn" onClick={handlePayment} disabled={loading}>
                {loading
                  ? <><span className="ck-spin"/> Processing…</>
                  : <><LockClosedIcon style={{width:'.88rem',height:'.88rem'}}/> Pay ₹{getCartTotal()} via Razorpay</>}
              </button>
              <div className="ck-pay-note">Powered by Razorpay · UPI · Cards · Net Banking · Wallets</div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FridgeCheckout;